import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n";
import api from "../lib/api";
import { inr } from "../lib/format";
import { PrimaryButton, WhyBanner } from "../components/common";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { ChevronLeft, Loader2 } from "lucide-react";

export default function AgreementCreate() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const listingId = sp.get("listing");
  const renewFrom = sp.get("renew");
  const [step, setStep] = useState(1);
  const [states, setStates] = useState([]);
  const [rule, setRule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    listing_id: listingId || null, tenant_email: "", state: "Maharashtra", property_address: "",
    rent: "", deposit: "", tenancy_months: 11, notice_period_months: 1, lockin_months: 0,
    maintenance_by: "Tenant", repairs_minor_by: "Tenant", repairs_major_by: "Owner",
  });

  useEffect(() => { api.get("/config/rules").then((r) => setStates(r.data.state_list)); }, []);
  useEffect(() => {
    if (listingId) api.get(`/listings/${listingId}`).then((r) => {
      const l = r.data;
      setF((s) => ({ ...s, state: l.state, property_address: `${l.title}, ${l.locality}, ${l.city}`, rent: String(l.rent), deposit: String(l.deposit) }));
    });
  }, [listingId]);
  useEffect(() => {
    if (renewFrom) api.get(`/agreements/${renewFrom}`).then((r) => {
      const a = r.data;
      setF((s) => ({ ...s,
        state: a.state || s.state, property_address: a.property_address || s.property_address,
        tenant_email: a.tenant_email || "", rent: String(a.rent ?? s.rent), deposit: String(a.deposit ?? s.deposit),
        tenancy_months: a.tenancy_months ?? s.tenancy_months, notice_period_months: a.notice_period_months ?? s.notice_period_months,
        lockin_months: a.lockin_months ?? s.lockin_months, maintenance_by: a.maintenance_by || s.maintenance_by,
        repairs_minor_by: a.repairs_minor_by || s.repairs_minor_by, repairs_major_by: a.repairs_major_by || s.repairs_major_by,
        listing_id: a.listing_id || s.listing_id,
      }));
    }).catch(() => alert("Couldn't load the previous agreement details — please fill them in manually."));
  }, [renewFrom]);
  useEffect(() => { if (f.state) api.get(`/config/rules/${f.state}`).then((r) => setRule(r.data)); }, [f.state]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const cap = rule ? rule.deposit_cap_months * (Number(f.rent) || 0) : 0;
  const overCap = rule && Number(f.deposit) > cap;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agreements", {
        ...f, rent: Number(f.rent), deposit: Number(f.deposit), tenancy_months: Number(f.tenancy_months),
        notice_period_months: Number(f.notice_period_months), lockin_months: Number(f.lockin_months),
      });
      navigate(`/agreements/${data.id}`);
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md md:max-w-4xl mx-auto min-h-screen bg-sand pb-10">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}><ChevronLeft className="w-6 h-6 text-slate-600" /></button>
        <div className="flex-1"><div className="font-head font-bold text-slate-900">{t("gen_agreement")}</div><div className="text-xs text-slate-400">{t("step")} {step}/3</div></div>
      </header>
      <Progress value={(step / 3) * 100} className="h-1.5 rounded-none" />

      <div className="px-4 py-5 space-y-3">
        {renewFrom && <div className="gs-card p-3 text-xs bg-terracotta-light text-terracotta-dark font-semibold" data-testid="renewal-draft-banner">Renewing your tenancy — details copied from the ending agreement. Review, update anything, then create.</div>}
        {rule && <div className="gs-card p-3 text-xs text-slate-500">{rule.law} · {rule.mta_adopted ? "Model Tenancy Act adopted" : "State Rent Control Act"} · Deposit cap {rule.deposit_cap_months} months</div>}

        {step === 1 && (
          <div className="space-y-3 gs-rise">
            <Field label={t("state")}><select className="w-full rounded-xl border border-slate-200 gs-tap px-3 text-sm" data-testid="agr-state" value={f.state} onChange={(e) => set("state", e.target.value)}>{states.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Property address"><Input className="rounded-xl gs-tap" data-testid="agr-address" value={f.property_address} onChange={(e) => set("property_address", e.target.value)} /></Field>
            <Field label="Tenant email"><Input className="rounded-xl gs-tap" data-testid="agr-tenant-email" value={f.tenant_email} onChange={(e) => set("tenant_email", e.target.value)} placeholder="tenant@email.com" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("rent")}><Input type="number" className="rounded-xl gs-tap" data-testid="agr-rent" value={f.rent} onChange={(e) => set("rent", e.target.value)} /></Field>
              <Field label={t("deposit")}><Input type="number" className="rounded-xl gs-tap" data-testid="agr-deposit" value={f.deposit} onChange={(e) => set("deposit", e.target.value)} /></Field>
            </div>
            {overCap && <WhyBanner>Deposit {inr(Number(f.deposit))} is above the lawful cap of {rule.deposit_cap_months} months ({inr(cap)}) in {f.state}. You can continue, but it may be challenged.</WhyBanner>}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 gs-rise">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Months"><Input type="number" className="rounded-xl gs-tap" data-testid="agr-months" value={f.tenancy_months} onChange={(e) => set("tenancy_months", e.target.value)} /></Field>
              <Field label="Notice (m)"><Input type="number" className="rounded-xl gs-tap" data-testid="agr-notice" value={f.notice_period_months} onChange={(e) => set("notice_period_months", e.target.value)} /></Field>
              <Field label="Lock-in (m)"><Input type="number" className="rounded-xl gs-tap" data-testid="agr-lockin" value={f.lockin_months} onChange={(e) => set("lockin_months", e.target.value)} /></Field>
            </div>
            <Field label="Maintenance paid by"><Choice v={f.maintenance_by} set={(x) => set("maintenance_by", x)} opts={MAINT_OPTS} tid="agr-maint" /></Field>
            <Field label="Minor repairs by"><Choice v={f.repairs_minor_by} set={(x) => set("repairs_minor_by", x)} opts={MAINT_OPTS} tid="agr-minor" /></Field>
            <Field label="Major repairs by"><Choice v={f.repairs_major_by} set={(x) => set("repairs_major_by", x)} opts={REPAIR_MAJOR_OPTS} tid="agr-major" /></Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 gs-rise">
            <div className="gs-card p-4 text-sm space-y-1.5">
              <Row k={t("state")} v={f.state} /><Row k={t("rent")} v={inr(Number(f.rent))} /><Row k={t("deposit")} v={inr(Number(f.deposit))} />
              <Row k="Tenancy" v={`${f.tenancy_months} months`} /><Row k="Notice" v={`${f.notice_period_months} month(s)`} /><Row k="Maintenance" v={f.maintenance_by} />
            </div>
            <WhyBanner>After creating, both sides tap-explain each clause and e-Sign with Aadhaar OTP.</WhyBanner>
            <PrimaryButton onClick={create} disabled={busy || !f.rent || !f.property_address} data-testid="agr-create-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t("gen_agreement")}</PrimaryButton>
          </div>
        )}

        {step < 3 && <PrimaryButton onClick={() => setStep(step + 1)} data-testid="agr-next-btn">{t("next")}</PrimaryButton>}
      </div>
    </div>
  );
}
const Field = ({ label, children }) => <div><label className="text-xs font-semibold text-slate-500 mb-1.5 block">{label}</label>{children}</div>;
const Row = ({ k, v }) => <div className="flex justify-between"><span className="text-slate-400">{k}</span><span className="font-semibold text-slate-800">{v}</span></div>;
const MAINT_OPTS = ["Tenant", "Owner"];
const REPAIR_MAJOR_OPTS = ["Owner", "Tenant"];
const Choice = ({ v, set, opts, tid }) => (
  <div className="flex gap-2">{opts.map((o) => <button key={o} onClick={() => set(o)} data-testid={`${tid}-${o}`} className={`flex-1 gs-tap rounded-xl border text-sm font-semibold ${v === o ? "border-terracotta bg-terracotta-light text-terracotta-dark" : "border-slate-200 text-slate-500"}`}>{o}</button>)}</div>
);
